import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TerminalView } from '../components/Terminal';
import { api } from '../lib/api';
import type { ContextUsage } from '../lib/n4';
import { ModelEffort } from '../components/n4/ModelEffort';

interface Project {
  id: number;
  name: string;
}
interface SessionInfo {
  id: string;
  name: string;
  projectId: number | null;
  kind?: 'main' | 'worker';
  hiddenInTerminal?: boolean;
}
interface Tab {
  key: string;
  projectId: number | null;
  name: string;
  sessionId?: string;
  ended?: boolean;
}

export function TerminalPage() {
  const [params] = useSearchParams();
  const projectParam = params.get('project');
  const wantProjectId = projectParam ? Number(projectParam) : null;

  const [projects, setProjects] = useState<Project[]>([]);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const readyRef = useRef(false);
  const navigate = useNavigate();
  // Jobb klikkes menü a füleken (projekt megnyitása, bezárás).
  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    window.addEventListener('mousedown', close); window.addEventListener('scroll', close, true); window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', close); window.removeEventListener('scroll', close, true); window.removeEventListener('keydown', onKey); };
  }, [menu]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const tabsRef = useRef<Tab[]>([]);
  tabsRef.current = tabs;

  // Az aktív fül ablakának modellje és effortja (a fül-sáv jobb szélén), az átiratából, 8 mp-enként frissítve.
  const [ctx, setCtx] = useState<ContextUsage | null>(null);
  const activeSid = tabs.find((t) => t.key === activeKey)?.sessionId;
  useEffect(() => {
    if (!activeSid) { setCtx(null); return undefined; }
    let stop = false;
    const load = () => { api<ContextUsage>(`/api/sessions/${activeSid}/context`).then((c) => { if (!stop) setCtx(c); }).catch(() => {}); };
    load(); const t = setInterval(load, 8_000);
    return () => { stop = true; clearInterval(t); };
  }, [activeSid]);

  // Kezdeti fülek: a meglévő aktív sessionök + a kért projekt (vagy egy alap fül).
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api<{ projects: Project[] }>('/api/projects').catch(() => ({ projects: [] })),
      api<{ sessions: SessionInfo[] }>('/api/sessions').catch(() => ({ sessions: [] })),
    ]).then(([p, s]) => {
      if (cancelled) return;
      setProjects(p.projects);
      // A munkások a projekt Workflow fülén látszanak, ide csak a fő ablakok és a szabad terminálok kerülnek.
      // A korábban bezárt fülek (a szerver jegyzi) nem jönnek vissza; kivéve, ha épp azt a projektet nyitjuk meg.
      const wanted = s.sessions.find((se) => se.kind !== 'worker' && se.hiddenInTerminal && wantProjectId != null && se.projectId === wantProjectId);
      if (wanted) { wanted.hiddenInTerminal = false; void api(`/api/sessions/${wanted.id}`, { method: 'PATCH', body: JSON.stringify({ hiddenInTerminal: false }) }).catch(() => {}); }
      const initial: Tab[] = s.sessions.filter((se) => se.kind !== 'worker' && !se.hiddenInTerminal).map((se) => ({
        key: se.id,
        projectId: se.projectId,
        name: se.name,
        sessionId: se.id,
      }));
      let focus: string | null = null;
      if (wantProjectId != null) {
        const existing = initial.find((t) => t.projectId === wantProjectId);
        if (existing) focus = existing.key;
        else {
          const proj = p.projects.find((pr) => pr.id === wantProjectId);
          const key = crypto.randomUUID();
          initial.push({ key, projectId: wantProjectId, name: proj?.name ?? `projekt ${wantProjectId}` });
          focus = key;
        }
      }
      if (initial.length === 0) {
        const key = crypto.randomUUID();
        initial.push({ key, projectId: null, name: 'Terminál' });
        focus = key;
      }
      setTabs(initial);
      setActiveKey(focus ?? initial[0]?.key ?? null);
      readyRef.current = true;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Projekt-terminál nyitása menüből (?project= változik) → fül nyitása/fókusz.
  useEffect(() => {
    if (!readyRef.current || wantProjectId == null) return;
    const existing = tabsRef.current.find((t) => t.projectId === wantProjectId);
    if (existing) {
      setActiveKey(existing.key);
      return;
    }
    const proj = projects.find((pr) => pr.id === wantProjectId);
    const key = crypto.randomUUID();
    setTabs((prev) => [...prev, { key, projectId: wantProjectId, name: proj?.name ?? `projekt ${wantProjectId}` }]);
    setActiveKey(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectParam]);

  // Ha minden fül bezárult, nyissunk egy alapot.
  useEffect(() => {
    if (readyRef.current && tabs.length === 0) {
      const key = crypto.randomUUID();
      setTabs([{ key, projectId: null, name: 'Terminál' }]);
      setActiveKey(key);
    }
  }, [tabs.length]);

  const addTab = () => {
    const key = crypto.randomUUID();
    setTabs((prev) => [...prev, { key, projectId: null, name: 'Terminál' }]);
    setActiveKey(key);
  };

  const updateTabSession = (key: string, sessionId: string) => {
    setTabs((prev) => prev.map((t) => (t.key === key ? { ...t, sessionId } : t)));
    // Újra megnyitott (korábban bezárt) projekt-ablak: újra látszódjon újratöltés után is.
    void api(`/api/sessions/${sessionId}`, { method: 'PATCH', body: JSON.stringify({ hiddenInTerminal: false }) }).catch(() => {});
  };

  // Fül bezárása (×, vagy a menü „Bezárás”): az ablak RENDESEN lezárul, mint a /exit-tel — a projekt-oldal azonnal az
  // „Ügynök indítása” gombot mutatja, a beszélgetés megmarad és folytatható. A menü „Elrejtés” pontja csak a fület veszi le,
  // az ügynök fut tovább (a szerver megjegyzi, újratöltés után sem jön vissza).
  const closeTab = (key: string, mode: 'stop' | 'hide' = 'stop') => {
    const t = tabsRef.current.find((x) => x.key === key);
    if (t?.sessionId && !t.ended && mode === 'stop') {
      if (!confirm(t.projectId == null ? 'Bezárod ezt a terminált? A benne futó ügynök leáll.' : `Bezárod a(z) ${t.name} ablakát? Az ügynök leáll (mint a /exit), a beszélgetés megmarad, a projektnél folytatható.`)) return;
      void api(`/api/sessions/${t.sessionId}`, { method: 'DELETE' }).catch(() => {});
    } else if (t?.sessionId && !t.ended) {
      void api(`/api/sessions/${t.sessionId}`, { method: 'PATCH', body: JSON.stringify({ hiddenInTerminal: true }) }).catch(() => {});
    }
    const remaining = tabsRef.current.filter((x) => x.key !== key);
    setTabs(remaining);
    setActiveKey((prev) => (prev === key ? (remaining[0]?.key ?? null) : prev));
  };

  const active = tabs.find((t) => t.key === activeKey);
  // Előre megírt üzenet beküldése az aktív ablakba (több sor, Ctrl+Enter is küld).
  const sendDraft = async () => {
    const text = draft.trim(); if (!text || !active?.sessionId || active.ended) return;
    setSending(true);
    try { await api(`/api/sessions/${active.sessionId}/say`, { method: 'POST', body: JSON.stringify({ text }) }); setDraft(''); }
    catch { alert('Nem sikerült beküldeni (az ablak már nem él?).'); } finally { setSending(false); }
  };

  return (
    <div className="flex h-full flex-col p-3 sm:p-5">
      {/* Fül-sáv */}
      <div className="a-ttabs">
        {tabs.map((t) => (
          <div
            key={t.key}
            onClick={() => setActiveKey(t.key)}
            onContextMenu={(e) => { e.preventDefault(); setActiveKey(t.key); setMenu({ key: t.key, x: Math.min(e.clientX, window.innerWidth - 220), y: e.clientY }); }}
            onDoubleClick={() => { if (t.projectId != null) navigate(`/projects/${t.projectId}`); }}
            title={t.projectId != null ? 'Jobb klikk: projekt megnyitása (dupla kattintás is)' : undefined}
            className={`a-ttab ${t.key === activeKey ? 'on' : ''}`}
          >
            <i className={`a-lamp ${t.ended ? '' : t.projectId != null ? 'on' : 'idle'}`} />
            <span className="max-w-40 truncate">{t.name}{t.ended ? ' (vége)' : ''}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeTab(t.key);
              }}
              className="x"
              title={t.sessionId && !t.ended ? 'Bezárás — az ügynök leáll (/exit)' : 'Bezárás'}
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={addTab}
          className="a-ttab add"
          title="Új terminál"
        >
          +
        </button>
        {ctx?.known && (ctx.model || ctx.effort) ? <div className="a-ttabinfo" title="Az aktív ablak modellje és effortja (az átiratából)"><ModelEffort model={ctx.model} effort={ctx.effort} /></div> : null}
      </div>

      {/* Terminál-panelek: mind él, csak az aktív látszik (váltáskor a pty nem szakad meg) */}
      <div className="relative min-h-0 flex-1">
        {tabs.map((t) => (
          <div
            key={t.key}
            className="absolute inset-0"
            style={{ display: t.key === activeKey ? 'block' : 'none' }}
          >
            <div className="a-termwrap h-full overflow-hidden p-2">
              <TerminalView
                projectId={t.projectId}
                name={t.name}
                sessionId={t.sessionId}
                onSession={(sid) => updateTabSession(t.key, sid)}
                onGone={() => setTabs((prev) => prev.map((x) => (x.key === t.key ? { ...x, ended: true } : x)))}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex shrink-0 gap-2">
        <textarea className="a-input min-h-[72px] flex-1 resize-y font-mono text-[12.5px] sm:min-h-[100px]" placeholder="Írd meg itt nyugodtan, több sorban — a Küldés (vagy Ctrl+Enter) egyben beküldi az aktív ablaknak." value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void sendDraft(); } }} />
        <button className="a-btn pri" disabled={sending || !draft.trim() || !active?.sessionId || active.ended} onClick={() => void sendDraft()} title="Ctrl+Enter">{sending ? 'Küldés…' : 'Küldés'}</button>
      </div>
      {menu && (() => {
        const t = tabs.find((x) => x.key === menu.key); if (!t) return null;
        const item = '';
        return (
          <div className="a-menu fixed z-50" style={{ left: menu.x, top: menu.y }} onMouseDown={(e) => e.stopPropagation()}>
            <div role="button" tabIndex={0} aria-disabled={t.projectId == null} className={`${item} cursor-pointer ${t.projectId == null ? 'pointer-events-none text-neutral-600' : ''}`} onClick={() => { setMenu(null); if (t.projectId != null) navigate(`/projects/${t.projectId}`); }}>Projekt megnyitása{t.projectId == null ? ' (nincs projekthez kötve)' : ''}</div>
            {t.projectId != null && <div role="button" tabIndex={0} className={`${item} cursor-pointer`} onClick={() => { setMenu(null); navigate(`/projects/${t.projectId}?tab=repo`); }}>Projekt repója</div>}
            {t.projectId != null && t.sessionId && !t.ended && <div role="button" tabIndex={0} className={`${item} cursor-pointer`} onClick={() => { setMenu(null); closeTab(t.key, 'hide'); }}>Fül elrejtése (az ügynök fut tovább)</div>}
            <div role="button" tabIndex={0} className={t.sessionId && !t.ended ? 'danger' : `${item} cursor-pointer`} onClick={() => { setMenu(null); closeTab(t.key); }}>{t.sessionId && !t.ended ? 'Bezárás — az ügynök leáll (/exit)' : 'Fül bezárása'}</div>
          </div>
        );
      })()}
    </div>
  );
}
